import { authFetch } from '@/lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Edit3, Save, X, Plus, Trash2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GuideEntreprisePage() {
    const navigate = useNavigate();
    const isAdmin = JSON.parse(localStorage.getItem('guide_user') || '{}').role === 'admin';
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editSections, setEditSections] = useState([]);
    const [saving, setSaving] = useState(false);

    const fetchPage = useCallback(async () => {
        try {
            const res = await authFetch(`${API}/api/guide/pages/entreprise`);
            const data = await res.json();
            setPage(data);
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchPage(); }, [fetchPage]);

    const startEdit = () => {
        setEditSections(JSON.parse(JSON.stringify(page?.sections || [])));
        setEditing(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await authFetch(`${API}/api/guide/pages/entreprise`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sections: editSections }),
            });
            const data = await res.json();
            setPage(data);
            setEditing(false);
            toast.success('Page mise a jour');
        } catch { toast.error('Erreur'); }
        finally { setSaving(false); }
    };

    const addSection = () => {
        setEditSections(prev => [...prev, { id: '', title: '', content: '' }]);
    };

    const removeSection = (idx) => {
        setEditSections(prev => prev.filter((_, i) => i !== idx));
    };

    const updateSection = (idx, field, value) => {
        setEditSections(prev => {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], [field]: value };
            return copy;
        });
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    return (
        <div className="px-4 py-5" data-testid="guide-entreprise">
            <button onClick={() => navigate('/guide')} className="flex items-center gap-1 text-sm text-slate-500 mb-4 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Accueil
            </button>

            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-slate-900">{page?.title || 'Fuji Express'}</h1>
                {!editing && isAdmin && (
                    <Button size="sm" variant="ghost" className="text-slate-500 text-xs" onClick={startEdit} data-testid="edit-page-btn">
                        <Edit3 className="w-3.5 h-3.5 mr-1" />Modifier
                    </Button>
                )}
            </div>

            {editing ? (
                <div className="space-y-3">
                    {editSections.map((section, i) => (
                        <div key={i} className="bg-white rounded-xl border border-slate-200 p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <Input value={section.title} onChange={e => updateSection(i, 'title', e.target.value)} placeholder="Titre..." className="h-8 text-sm font-semibold flex-1" />
                                <button onClick={() => removeSection(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <textarea
                                value={section.content}
                                onChange={e => updateSection(i, 'content', e.target.value)}
                                placeholder="Contenu..."
                                className="w-full text-sm border border-slate-200 rounded-lg p-2 min-h-[80px] resize-y"
                            />
                        </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full" onClick={addSection}>
                        <Plus className="w-4 h-4 mr-1" />Ajouter une section
                    </Button>
                    <div className="flex gap-2 pt-2">
                        <Button variant="outline" className="flex-1" onClick={() => setEditing(false)}><X className="w-4 h-4 mr-1" />Annuler</Button>
                        <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleSave} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Sauvegarder
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    {(page?.sections || []).map((section) => (
                        <div key={section.id} className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
                            <h2 className="font-semibold text-slate-900 text-sm mb-1">{section.title}</h2>
                            <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">{section.content}</p>
                        </div>
                    ))}
                    {(!page?.sections || page.sections.length === 0) && (
                        <p className="text-sm text-slate-400 text-center py-8">Aucun contenu. Cliquez sur Modifier pour ajouter du contenu.</p>
                    )}
                </div>
            )}
        </div>
    );
}
